import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Star, ExternalLink, Quote, Loader2, ShieldCheck } from "lucide-react";
import api from "../lib/api";

const SOURCE_STYLE = {
  google: { label: "Google Review", bg: "bg-[#e8eefb]", fg: "text-[#1a3f8f]" },
  facebook: { label: "Facebook Review", bg: "bg-[#e7edf8]", fg: "text-[#1b4b9c]" },
  angi: { label: "Angi Review", bg: "bg-[#fdeaea]", fg: "text-[#a10000]" },
  bbb: { label: "BBB Commendation", bg: "bg-[#eef2f7]", fg: "text-[#0c2a66]" },
  testimonial: { label: "Customer Testimonial", bg: "bg-[#f1f1f1]", fg: "text-[#575757]" },
};

function Stars({ rating = 5 }) {
  return (
    <span className="flex shrink-0 gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i <= rating ? "fill-[#f0a92b] text-[#f0a92b]" : "text-neutral-300"}`}
        />
      ))}
    </span>
  );
}

function Initials({ name }) {
  const initials = (name || "?")
    .split(/[\s,]+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#244aa2] text-[13px] font-bold text-white">
      {initials}
    </span>
  );
}

/* Original review date from the stored record. Blank when none is stored. */
function reviewDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

function ReviewCard({ r }) {
  const [expanded, setExpanded] = useState(false);
  const style = SOURCE_STYLE[r.source] || SOURCE_STYLE.testimonial;
  const long = (r.content || "").length > 420;
  const text = expanded || !long ? r.content : `${r.content.slice(0, 420).trimEnd()}…`;
  const date = reviewDate(r.published_at);

  return (
    <article data-testid="review-card" className="flex h-full min-w-0 flex-col overflow-hidden border border-neutral-200 bg-white p-6 shadow-sm transition-shadow duration-300 hover:shadow-lg">
      <header className="flex items-start gap-3">
        {r.author_photo ? (
          <img
            src={r.author_photo}
            alt={r.author}
            className="h-10 w-10 shrink-0 rounded-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <Initials name={r.author} />
        )}
        <div className="min-w-0 flex-1">
          <h3 data-testid="review-author" className="font-head truncate text-[15px] font-extrabold text-[#242424]">{r.author}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Stars rating={r.rating} />
            {date && <span className="text-[12px] text-neutral-500" data-testid="review-date">{date}</span>}
          </div>
        </div>
      </header>

      <div className="mt-4 flex flex-1 gap-2.5">
        <Quote className="h-5 w-5 shrink-0 text-[#244aa2]/35" />
        <p data-testid="review-content" className="min-w-0 break-words text-[14.5px] leading-relaxed text-[#575757]">{text}</p>
      </div>

      {long && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 self-start text-[12px] font-bold uppercase tracking-wider text-[#244aa2] underline transition-colors hover:text-[#a10000]"
        >
          {expanded ? "Show less" : "Read full review"}
        </button>
      )}

      {r.response && (
        <div className="mt-4 border-l-2 border-[#244aa2] bg-[#f7f7f7] p-3">
          <p className="text-[12px] font-bold uppercase tracking-wide text-[#244aa2]">
            Response from Mr. Resurface
          </p>
          <p className="mt-1 text-[14px] leading-relaxed text-[#575757]">{r.response}</p>
        </div>
      )}

      <footer className="mt-5 flex items-center justify-between gap-3 border-t border-neutral-200 pt-4">
        <span
          data-testid="review-source-label"
          className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${style.bg} ${style.fg}`}
        >
          {r.source_label || style.label}
        </span>
        {r.url && (
          <a
            href={r.url}
            target="_blank"
            rel="noreferrer nofollow"
            className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider text-[#244aa2] transition-colors hover:text-[#a10000]"
          >
            View original <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </footer>
    </article>
  );
}

const PAGE = 12;

export default function ReviewWall() {
  const [stats, setStats] = useState(null);
  const [source, setSource] = useState("all");
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/reviews/stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
  }, []);

  const load = useCallback(
    async (nextSource, skip) => {
      setLoading(true);
      try {
        const { data } = await api.get("/reviews", {
          params: { source: nextSource, limit: PAGE, skip },
        });
        setTotal(data.total);
        setReviews((prev) => (skip === 0 ? data.reviews : [...prev, ...data.reviews]));
        setError(null);
      } catch {
        setError("Reviews could not be loaded right now. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load(source, 0);
  }, [source, load]);

  const tabs = useMemo(() => {
    const by = stats?.by_source || {};
    return [
      { key: "all", label: "All reviews", count: stats?.total },
      { key: "google", label: "Google", count: by.google?.count },
      { key: "angi", label: "Angi", count: by.angi?.count },
      { key: "facebook", label: "Facebook", count: by.facebook?.count },
      { key: "testimonial", label: "Website testimonials", count: by.testimonial?.count },
      { key: "bbb", label: "BBB", count: by.bbb?.count },
    ].filter((t) => t.key === "all" || t.count);
  }, [stats]);

  return (
    <section className="bg-[#f7f7f7] py-16" id="reviews" data-testid="review-wall">
      <div className="mr-container-wide">
        {/* summary */}
        <div className="reveal flex flex-col items-center gap-4 border border-neutral-200 bg-white p-8 text-center shadow-sm">
          <p className="font-head text-[12px] font-extrabold uppercase tracking-[0.2em] text-[#a10000]">
            Reviews from our customers
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <span className="font-head text-5xl font-extrabold leading-none text-[#244aa2]">
              {stats ? stats.average.toFixed(1) : "—"}
            </span>
            <div className="text-left">
              <Stars rating={Math.round(stats?.average || 5)} />
              <p className="mt-1 text-[14px] font-semibold text-[#575757]" data-testid="review-total">
                {stats ? `${stats.total} verified reviews` : "Loading…"}
              </p>
            </div>
          </div>
          <p className="flex items-center gap-2 text-[13px] text-neutral-500">
            <ShieldCheck className="h-4 w-4 text-[#244aa2]" />
            Every review is shown in the customer&rsquo;s own words with its original source. We never
            filter or gate reviews.
          </p>
        </div>

        {/* filters */}
        <div className="reveal mt-8 flex flex-wrap justify-center gap-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`review-filter-${t.key}`}
              onClick={() => setSource(t.key)}
              aria-pressed={source === t.key}
              className={`px-4 py-2.5 text-[12px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                source === t.key
                  ? "bg-[#244aa2] text-white"
                  : "border border-[#244aa2] bg-white text-[#244aa2] hover:bg-[#e8eefb]"
              }`}
            >
              {t.label}
              {t.count ? ` (${t.count})` : ""}
            </button>
          ))}
        </div>

        {error && (
          <p className="mt-8 text-center font-semibold text-[#a10000]" role="alert">
            {error}
          </p>
        )}

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r) => (
            <ReviewCard key={r.id} r={r} />
          ))}
        </div>

        {loading && (
          <div className="mt-10 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-[#244aa2]" />
          </div>
        )}

        {!loading && reviews.length < total && (
          <div className="mt-10 flex flex-col items-center gap-3">
            <button
              data-testid="review-load-more"
              onClick={() => load(source, reviews.length)}
              className="bg-[#244aa2] px-9 py-4 text-[12px] font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:bg-[#0c2a66]"
            >
              Load more reviews
            </button>
            <p className="text-[13px] text-neutral-500">
              Showing {reviews.length} of {total}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
